import React, { useRef } from "react";

/**
 * `React.memo` for the Lab's heavy sections, with stable callback props.
 *
 * The Lab page holds all of its state in one component, so every change (a keystroke in the
 * TES box, opening Settings, switching tabs) re-renders every section — and the charts in them
 * redo their layout and settings work each time. Plain `React.memo` can't help because the page
 * passes fresh inline callbacks on every render. This wrapper hands the section stable
 * functions that always call the latest prop, so the section re-renders only when its data
 * changes.
 *
 * Function props the section calls while rendering (e.g. a label formatter) must be listed in
 * `renderProps`: they are passed through as-is, so a new one re-renders the section.
 */
export function strategyRdMemo<P extends object>(
  Component: React.ComponentType<P>,
  renderProps: ReadonlyArray<keyof P & string> = []
): React.FC<P> {
  const Inner = React.memo(Component) as unknown as React.ComponentType<P>;
  function StableCallbacks(props: P): JSX.Element {
    const latest = useRef(props);
    latest.current = props;
    const stable = useRef<Record<string, (...args: unknown[]) => unknown>>({});
    const next: Record<string, unknown> = {};
    for (const key of Object.keys(props)) {
      const value = (props as Record<string, unknown>)[key];
      if (typeof value === "function" && !renderProps.includes(key as keyof P & string)) {
        if (!stable.current[key]) {
          stable.current[key] = (...args: unknown[]) =>
            ((latest.current as Record<string, unknown>)[key] as (...a: unknown[]) => unknown)(...args);
        }
        next[key] = stable.current[key];
      } else {
        next[key] = value;
      }
    }
    return <Inner {...(next as P)} />;
  }
  StableCallbacks.displayName = `StrategyRdMemo(${Component.displayName || Component.name || "Component"})`;
  return StableCallbacks;
}

/**
 * Children of a hidden tab: rendered while `active`, frozen (no re-render) while hidden — the
 * results tabs all stay mounted (a running test keeps polling off-tab), and without this every
 * click re-rendered every tab. Effects and state inside keep running; when the tab shows again
 * it renders once with the latest props.
 */
export const StrategyRdWhenVisible = React.memo(
  function StrategyRdWhenVisible({ children }: { active: boolean; children: React.ReactNode }): JSX.Element {
    return <>{children}</>;
  },
  (_prev, next) => !next.active
);
